import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { formatApiDateTime } from '../admin/dateTimeUtils';

type FolderManagementPanelProps = {
    baseUrl: string;
    onResponse: (value: unknown) => void;
};

type FileRoot = {
    root: string;
    path: string;
    exists: boolean;
};

type FileEntry = {
    name: string;
    path: string;
    root: string;
    type: string;
    size: number;
    modified_at: string;
    children?: FileEntry[];
};

type FileAction = 'create-folder' | 'rename' | 'move' | 'copy';

type ActionDraft = {
    open: boolean;
    action: FileAction;
    name: string;
    targetRoot: string;
    targetPath: string;
    overwrite: boolean;
};

const emptyActionDraft: ActionDraft = {
    open: false,
    action: 'create-folder',
    name: '',
    targetRoot: 'outputs',
    targetPath: '',
    overwrite: false,
};

type MediaKind = 'image' | 'audio' | 'video';

type PreviewState = {
    open: boolean;
    entry: FileEntry | null;
    kind: MediaKind | null;
    url: string;
    mimeType: string;
};

const emptyPreviewState: PreviewState = {
    open: false,
    entry: null,
    kind: null,
    url: '',
    mimeType: '',
};

const idleRefreshMs = 5_000;
const fileExplorerRowLimit = 10;

function trimTrailingSlash(value: string): string {
    return value.trim().replace(/\/+$/, '');
}

function normalizePath(value: string): string {
    return value.trim().replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
}

function joinPath(parent: string, child: string): string {
    const left = normalizePath(parent);
    const right = normalizePath(child);
    if (!left) return right;
    if (!right) return left;
    return `${left}/${right}`;
}

function parentPath(path: string): string {
    const normalized = normalizePath(path);
    const index = normalized.lastIndexOf('/');
    return index === -1 ? '' : normalized.slice(0, index);
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const record = asRecord(payload);
    const message = record.detail ?? record.message ?? record.error ?? record.errorMessage;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

async function requestFolderApi<T>(baseUrl: string, path: string, init: RequestInit = {}): Promise<T> {
    const url = `${trimTrailingSlash(baseUrl)}${path}`;
    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }

    const response = await fetch(url, { ...init, headers });
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return payload as T;
}

function normalizeEntry(value: unknown): FileEntry {
    const record = asRecord(value);
    const type = asString(record.type, Boolean(record.isDirectory ?? record.is_dir) ? 'directory' : 'file');
    const children = Array.isArray(record.children) ? record.children.map(normalizeEntry) : undefined;

    return {
        name: asString(record.name || record.fileName || record.path),
        path: normalizePath(asString(record.path)),
        root: asString(record.root),
        type,
        size: asNumber(record.size ?? record.sizeBytes, 0),
        modified_at: asString(record.modified_at ?? record.modifiedAt),
        children,
    };
}

function normalizeEntries(value: unknown): FileEntry[] {
    const payload = Array.isArray(value) ? value : asRecord(value).children;
    return Array.isArray(payload) ? payload.map(normalizeEntry) : [];
}

function getModifiedTime(entry: FileEntry): number {
    const time = Date.parse(entry.modified_at);
    return Number.isFinite(time) ? time : 0;
}

function getLatestEntries(entries: FileEntry[]): FileEntry[] {
    return [...entries]
        .sort((left, right) => getModifiedTime(right) - getModifiedTime(left) || right.name.localeCompare(left.name))
        .slice(0, fileExplorerRowLimit);
}

function limitTreeEntry(entry: FileEntry): FileEntry {
    if (!entry.children) return entry;

    return {
        ...entry,
        children: getLatestEntries(entry.children).map(limitTreeEntry),
    };
}

function normalizeRoots(value: unknown): FileRoot[] {
    if (!Array.isArray(value)) return [];
    return value.map((item) => {
        const record = asRecord(item);
        return {
            root: asString(record.root),
            path: asString(record.path),
            exists: Boolean(record.exists),
        };
    });
}

function formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function isDirectory(entry: FileEntry): boolean {
    return entry.type === 'directory' || entry.type === 'folder';
}

function getFileExtension(entry: FileEntry): string {
    const name = entry.name || entry.path;
    const index = name.lastIndexOf('.');
    return index === -1 ? '' : name.slice(index + 1).toLowerCase();
}

function getMediaKind(entry: FileEntry): MediaKind | null {
    const extension = getFileExtension(entry);

    if (['apng', 'avif', 'bmp', 'gif', 'jpg', 'jpeg', 'png', 'svg', 'webp'].includes(extension)) {
        return 'image';
    }

    if (['aac', 'flac', 'm4a', 'mp3', 'oga', 'ogg', 'wav'].includes(extension)) {
        return 'audio';
    }

    if (['m4v', 'mov', 'mp4', 'mpeg', 'mpg', 'ogv', 'webm'].includes(extension)) {
        return 'video';
    }

    return null;
}

function getPreviewLabel(kind: MediaKind): string {
    return kind === 'image' ? 'View' : 'Play';
}

function getPreviewIcon(kind: MediaKind) {
    if (kind === 'image') return <VisibilityOutlinedIcon fontSize="small" />;
    return <PlayArrowRoundedIcon fontSize="small" />;
}

function getFallbackMimeType(entry: FileEntry, kind: MediaKind | null): string {
    const extension = getFileExtension(entry);
    const knownTypes: Record<string, string> = {
        aac: 'audio/aac',
        avif: 'image/avif',
        bmp: 'image/bmp',
        flac: 'audio/flac',
        gif: 'image/gif',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        m4a: 'audio/mp4',
        m4v: 'video/mp4',
        mov: 'video/quicktime',
        mp3: 'audio/mpeg',
        mp4: 'video/mp4',
        oga: 'audio/ogg',
        ogg: 'audio/ogg',
        ogv: 'video/ogg',
        png: 'image/png',
        svg: 'image/svg+xml',
        wav: 'audio/wav',
        webm: kind === 'audio' ? 'audio/webm' : 'video/webm',
        webp: 'image/webp',
    };

    return knownTypes[extension] ?? 'application/octet-stream';
}

function getDownloadFileName(entry: FileEntry, archive: boolean): string {
    const fallback = archive ? 'archive.zip' : 'download';
    const rawName = entry.name || entry.path.split('/').filter(Boolean).pop() || fallback;
    return archive && !rawName.toLowerCase().endsWith('.zip') ? `${rawName}.zip` : rawName;
}

function getFileApiUrl(baseUrl: string, entry: FileEntry, currentRoot: string, archive = false): string {
    const params = new URLSearchParams({ root: entry.root || currentRoot, path: entry.path });
    const endpoint = archive ? 'archive' : 'download';
    return `${trimTrailingSlash(baseUrl)}/api/files/${endpoint}?${params.toString()}`;
}

async function getFileBlob(baseUrl: string, entry: FileEntry, currentRoot: string, archive = false): Promise<Blob> {
    const response = await fetch(getFileApiUrl(baseUrl, entry, currentRoot, archive), {
        headers: { Accept: '*/*' },
    });

    if (!response.ok) {
        const contentType = response.headers.get('content-type') ?? '';
        const payload = contentType.includes('application/json') ? await response.json() : await response.text();
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    return response.blob();
}

function TreeNode({
    entry,
    depth,
    selectedPath,
    onOpen,
}: {
    entry: FileEntry;
    depth: number;
    selectedPath: string;
    onOpen: (path: string) => void;
}) {
    const folder = isDirectory(entry);

    return (
        <Box>
            <Button
                size="small"
                variant={selectedPath === entry.path ? 'contained' : 'text'}
                color={selectedPath === entry.path ? 'primary' : 'inherit'}
                startIcon={folder ? <FolderOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
                onClick={() => folder && onOpen(entry.path === '.' ? '' : entry.path)}
                sx={{
                    justifyContent: 'flex-start',
                    width: '100%',
                    pl: 1 + depth * 1.5,
                    textTransform: 'none',
                    overflow: 'hidden',
                }}
            >
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.name}
                </Box>
            </Button>
            {entry.children?.map((child) => (
                <TreeNode key={`${child.root}-${child.path}-${child.name}`} entry={child} depth={depth + 1} selectedPath={selectedPath} onOpen={onOpen} />
            ))}
        </Box>
    );
}

export function FolderManagementPanel({ baseUrl, onResponse }: FolderManagementPanelProps) {
    const notify = useNotify();
    const uploadInputRef = useRef<HTMLInputElement | null>(null);
    const nextIdleRefreshAtRef = useRef(Date.now() + idleRefreshMs);
    const [roots, setRoots] = useState<FileRoot[]>([]);
    const [root, setRoot] = useState('outputs');
    const [path, setPath] = useState('');
    const [children, setChildren] = useState<FileEntry[]>([]);
    const [tree, setTree] = useState<FileEntry | null>(null);
    const [selected, setSelected] = useState<FileEntry | null>(null);
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadFileName, setUploadFileName] = useState('');
    const [uploadOverwrite, setUploadOverwrite] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [draft, setDraft] = useState<ActionDraft>(emptyActionDraft);
    const [preview, setPreview] = useState<PreviewState>(emptyPreviewState);

    const markUserActivity = () => {
        nextIdleRefreshAtRef.current = Date.now() + idleRefreshMs;
    };

    const breadcrumbItems = useMemo(() => {
        const parts = normalizePath(path).split('/').filter(Boolean);
        return parts.map((part, index) => ({
            label: part,
            path: parts.slice(0, index + 1).join('/'),
        }));
    }, [path]);

    const runFolderRequest = async <T,>(action: () => Promise<T>, successMessage?: string): Promise<T | null> => {
        setLoading(true);
        setError('');

        try {
            const result = await action();
            onResponse(result);
            if (successMessage) {
                notify(successMessage, { type: 'info' });
            }
            return result;
        } catch (requestError) {
            const message = requestError instanceof Error ? requestError.message : 'Folder request failed';
            setError(message);
            notify(message, { type: 'warning' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    const loadRoots = async () => {
        const result = await runFolderRequest(async () => requestFolderApi<unknown>(baseUrl, '/api/files/roots'));
        const nextRoots = normalizeRoots(result);
        setRoots(nextRoots);

        if (nextRoots.length > 0 && !nextRoots.some((item) => item.root === root)) {
            setRoot(nextRoots[0].root);
        }
    };

    const loadChildren = async (nextRoot = root, nextPath = path) => {
        const params = new URLSearchParams({ root: nextRoot, path: normalizePath(nextPath) });
        const result = await runFolderRequest(async () => requestFolderApi<unknown>(baseUrl, `/api/files/children?${params.toString()}`));
        setChildren(getLatestEntries(normalizeEntries(result).filter((entry) => !isDirectory(entry))));
        setSelected(null);
    };

    const loadTree = async (nextRoot = root, nextPath = path) => {
        const params = new URLSearchParams({ root: nextRoot, path: normalizePath(nextPath), depth: '3' });
        const result = await runFolderRequest(async () => requestFolderApi<unknown>(baseUrl, `/api/files/tree?${params.toString()}`));
        setTree(result ? limitTreeEntry(normalizeEntry(result)) : null);
    };

    const refreshFolder = async () => {
        await loadChildren();
        await loadTree();
    };

    useEffect(() => {
        void loadRoots();
    }, [baseUrl]);

    useEffect(() => {
        void refreshFolder();
    }, [baseUrl, root, path]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            const shouldRefresh = Date.now() >= nextIdleRefreshAtRef.current && !loading && !draft.open;
            if (!shouldRefresh) {
                return;
            }

            nextIdleRefreshAtRef.current = Date.now() + idleRefreshMs;
            void refreshFolder();
        }, 1_000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [baseUrl, draft.open, loading, path, root]);

    useEffect(() => {
        return () => {
            if (preview.url) {
                window.URL.revokeObjectURL(preview.url);
            }
        };
    }, [preview.url]);

    const openFolder = (nextPath: string) => {
        setPath(normalizePath(nextPath));
    };

    const openAction = (action: FileAction) => {
        setDraft({
            open: true,
            action,
            name: action === 'rename' && selected ? selected.name : '',
            targetRoot: root,
            targetPath: selected ? joinPath(path, selected.name) : path,
            overwrite: false,
        });
    };

    const closeAction = () => {
        setDraft(emptyActionDraft);
    };

    const saveAction = async () => {
        if (draft.action === 'create-folder') {
            const folderPath = joinPath(path, draft.name);
            if (!folderPath) {
                notify('Folder path is required', { type: 'warning' });
                return;
            }

            await runFolderRequest(
                async () =>
                    requestFolderApi<unknown>(baseUrl, '/api/files/folders', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ root, path: folderPath }),
                    }),
                'Folder created',
            );
            closeAction();
            await refreshFolder();
            return;
        }

        if (!selected) {
            notify('Select a file or folder first', { type: 'warning' });
            return;
        }

        if (draft.action === 'rename') {
            await runFolderRequest(
                async () =>
                    requestFolderApi<unknown>(baseUrl, '/api/files/rename', {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            root,
                            path: selected.path,
                            new_name: draft.name.trim(),
                            overwrite: draft.overwrite,
                        }),
                    }),
                'Item renamed',
            );
        } else {
            const endpoint = draft.action === 'move' ? '/api/files/move' : '/api/files/copy';
            await runFolderRequest(
                async () =>
                    requestFolderApi<unknown>(baseUrl, endpoint, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            source_root: root,
                            source_path: selected.path,
                            target_root: draft.targetRoot,
                            target_path: normalizePath(draft.targetPath),
                            overwrite: draft.overwrite,
                        }),
                    }),
                draft.action === 'move' ? 'Item moved' : 'Item copied',
            );
        }

        closeAction();
        await refreshFolder();
    };

    const uploadSelectedFile = async () => {
        if (!uploadFile) {
            notify('Choose a file first', { type: 'warning' });
            return;
        }

        const formData = new FormData();
        formData.set('root', root);
        formData.set('path', normalizePath(path));
        formData.set('overwrite', String(uploadOverwrite));
        formData.set('file', uploadFile);
        if (uploadFileName.trim()) {
            formData.set('filename', uploadFileName.trim());
        }

        await runFolderRequest(
            async () =>
                requestFolderApi<unknown>(baseUrl, '/api/files/upload', {
                    method: 'POST',
                    body: formData,
                }),
            'File uploaded',
        );

        setUploadFile(null);
        setUploadFileName('');
        setUploadOverwrite(false);
        if (uploadInputRef.current) {
            uploadInputRef.current.value = '';
        }
        await refreshFolder();
    };

    const downloadEntry = async (entry: FileEntry | null, archive: boolean) => {
        if (!entry) {
            notify('Select a file or folder first', { type: 'warning' });
            return;
        }

        await runFolderRequest(
            async () => {
                const blob = await getFileBlob(baseUrl, entry, root, archive);
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = getDownloadFileName(entry, archive);
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                window.URL.revokeObjectURL(url);
                return { downloaded: entry.path, archive };
            },
            archive ? 'Archive downloaded' : 'File downloaded',
        );
    };

    const openPreview = async (entry: FileEntry, kind: MediaKind) => {
        await runFolderRequest(
            async () => {
                const blob = await getFileBlob(baseUrl, entry, root);
                const url = window.URL.createObjectURL(blob);
                setPreview((current) => {
                    if (current.url) {
                        window.URL.revokeObjectURL(current.url);
                    }

                    return {
                        open: true,
                        entry,
                        kind,
                        url,
                        mimeType: blob.type || getFallbackMimeType(entry, kind),
                    };
                });
                return { previewing: entry.path };
            },
        );
    };

    const closePreview = () => {
        setPreview((current) => {
            if (current.url) {
                window.URL.revokeObjectURL(current.url);
            }

            return emptyPreviewState;
        });
    };

    const deleteSelected = async () => {
        if (!selected) {
            notify('Select a file or folder first', { type: 'warning' });
            return;
        }

        if (!window.confirm(`Delete "${selected.path}"?`)) {
            return;
        }

        const params = new URLSearchParams({ root, path: selected.path });
        await runFolderRequest(
            async () =>
                requestFolderApi<unknown>(baseUrl, `/api/files?${params.toString()}`, {
                    method: 'DELETE',
                }),
            'Item deleted',
        );
        await refreshFolder();
    };

    return (
        <Paper
            variant="outlined"
            onPointerDown={markUserActivity}
            onKeyDown={markUserActivity}
            sx={{ overflow: 'hidden' }}
        >
            {loading ? <LinearProgress /> : null}
            <Stack spacing={0}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ p: 2.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6">Folder Management</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage input, outputs, and generated folders from the pipeline file API.
                        </Typography>
                    </Box>
                    <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={() => void refreshFolder()}>
                        Refresh
                    </Button>
                </Stack>

                {error ? (
                    <Box sx={{ px: 2.5, pb: 2 }}>
                        <Alert severity="warning">{error}</Alert>
                    </Box>
                ) : null}

                <Divider />

                <Stack direction={{ xs: 'column', lg: 'row' }} sx={{ minHeight: 520 }}>
                    <Box sx={{ width: { lg: 300 }, borderRight: { lg: '1px solid #e2e8f0' }, p: 2 }}>
                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="folder-root-label">Root</InputLabel>
                                <Select
                                    labelId="folder-root-label"
                                    label="Root"
                                    value={root}
                                    onChange={(event) => {
                                        setRoot(event.target.value);
                                        setPath('');
                                    }}
                                >
                                    {(roots.length > 0 ? roots : [{ root: 'input', path: 'input', exists: true }, { root: 'outputs', path: 'outputs', exists: true }, { root: 'generated', path: 'generated', exists: true }]).map((item) => (
                                        <MenuItem key={item.root} value={item.root}>
                                            {item.root}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {roots.map((item) => (
                                    <Chip
                                        key={item.root}
                                        size="small"
                                        label={item.root}
                                        color={item.exists ? 'success' : 'warning'}
                                        variant={root === item.root ? 'filled' : 'outlined'}
                                        onClick={() => {
                                            setRoot(item.root);
                                            setPath('');
                                        }}
                                    />
                                ))}
                            </Stack>

                            <Divider />

                            <Typography variant="overline" color="text.secondary">
                                Tree
                            </Typography>
                            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                                {tree ? (
                                    <TreeNode entry={tree} depth={0} selectedPath={path || '.'} onOpen={openFolder} />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No tree loaded.
                                    </Typography>
                                )}
                            </Box>
                        </Stack>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, p: 2.5 }}>
                        <Stack spacing={2.5}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                <Breadcrumbs sx={{ flex: 1, minWidth: 0 }}>
                                    <Link
                                        component="button"
                                        underline="hover"
                                        color={!path ? 'text.primary' : 'inherit'}
                                        onClick={() => openFolder('')}
                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                        <HomeOutlinedIcon fontSize="small" />
                                        {root}
                                    </Link>
                                    {breadcrumbItems.map((item) => (
                                        <Link
                                            key={item.path}
                                            component="button"
                                            underline="hover"
                                            color={path === item.path ? 'text.primary' : 'inherit'}
                                            onClick={() => openFolder(item.path)}
                                        >
                                            {item.label}
                                        </Link>
                                    ))}
                                </Breadcrumbs>
                                <Button variant="outlined" onClick={() => openFolder(parentPath(path))} disabled={!path}>
                                    Up
                                </Button>
                            </Stack>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Button variant="outlined" startIcon={<CreateNewFolderOutlinedIcon />} onClick={() => openAction('create-folder')}>
                                    New Folder
                                </Button>
                                <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => openAction('rename')} disabled={!selected}>
                                    Rename
                                </Button>
                                <Button variant="outlined" startIcon={<DriveFileMoveOutlinedIcon />} onClick={() => openAction('move')} disabled={!selected}>
                                    Move
                                </Button>
                                <Button variant="outlined" startIcon={<ContentCopyOutlinedIcon />} onClick={() => openAction('copy')} disabled={!selected}>
                                    Copy
                                </Button>
                                <Button variant="outlined" startIcon={<ArchiveOutlinedIcon />} onClick={() => void downloadEntry(selected, true)} disabled={!selected}>
                                    Archive
                                </Button>
                                <Button variant="outlined" color="error" startIcon={<DeleteOutlineOutlinedIcon />} onClick={() => void deleteSelected()} disabled={!selected}>
                                    Delete
                                </Button>
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} alignItems={{ xl: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<UploadFileRoundedIcon />}
                                        sx={{ justifyContent: 'flex-start', minWidth: { xl: 240 } }}
                                    >
                                        {uploadFile ? uploadFile.name : 'Choose File'}
                                        <input
                                            ref={uploadInputRef}
                                            hidden
                                            type="file"
                                            onChange={(event: ChangeEvent<HTMLInputElement>) => setUploadFile(event.target.files?.[0] ?? null)}
                                        />
                                    </Button>
                                    <TextField
                                        label="Optional Filename"
                                        value={uploadFileName}
                                        onChange={(event) => setUploadFileName(event.target.value)}
                                        size="small"
                                        sx={{ flex: 1 }}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={uploadOverwrite} onChange={(event) => setUploadOverwrite(event.target.checked)} />}
                                        label="Overwrite"
                                    />
                                    <Button variant="contained" onClick={() => void uploadSelectedFile()} disabled={!uploadFile}>
                                        Upload Here
                                    </Button>
                                </Stack>
                            </Paper>

                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Files</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {children.length === 0 ? (
                                            <TableRow>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No files in this folder.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            children.map((entry) => {
                                                const active = selected?.path === entry.path;
                                                const mediaKind = getMediaKind(entry);
                                                const imagePreviewUrl = mediaKind === 'image' ? getFileApiUrl(baseUrl, entry, root) : '';

                                                return (
                                                    <TableRow
                                                        key={`${entry.root}-${entry.path}`}
                                                        hover
                                                        selected={active}
                                                        onClick={() => setSelected(entry)}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell>
                                                            <Stack spacing={1}>
                                                                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                                                    {imagePreviewUrl ? (
                                                                        <Box
                                                                            component="button"
                                                                            type="button"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setSelected(entry);
                                                                                void openPreview(entry, 'image');
                                                                            }}
                                                                            sx={{
                                                                                width: 72,
                                                                                height: 54,
                                                                                flexShrink: 0,
                                                                                p: 0,
                                                                                border: '1px solid',
                                                                                borderColor: 'divider',
                                                                                borderRadius: 1,
                                                                                bgcolor: 'background.default',
                                                                                cursor: 'pointer',
                                                                                overflow: 'hidden',
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                component="img"
                                                                                src={imagePreviewUrl}
                                                                                alt={entry.name}
                                                                                loading="lazy"
                                                                                sx={{
                                                                                    display: 'block',
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover',
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    ) : (
                                                                        <Box sx={{ flexShrink: 0, lineHeight: 0, pt: 0.25 }}>
                                                                            <InsertDriveFileOutlinedIcon color="action" />
                                                                        </Box>
                                                                    )}
                                                                    <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere', minWidth: 0 }}>
                                                                        {entry.name}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1.5}
                                                                    alignItems="center"
                                                                    useFlexGap
                                                                    flexWrap="wrap"
                                                                    sx={{ pl: 4, color: 'text.secondary' }}
                                                                >
                                                                    <Typography variant="body2">{entry.type}</Typography>
                                                                    <Typography variant="body2">{formatFileSize(entry.size)}</Typography>
                                                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                                                        {formatApiDateTime(entry.modified_at)}
                                                                    </Typography>
                                                                    {mediaKind && mediaKind !== 'image' ? (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={getPreviewIcon(mediaKind)}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setSelected(entry);
                                                                                void openPreview(entry, mediaKind);
                                                                            }}
                                                                            sx={{ minHeight: 30 }}
                                                                        >
                                                                            {getPreviewLabel(mediaKind)}
                                                                        </Button>
                                                                    ) : null}
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            setSelected(entry);
                                                                            void downloadEntry(entry, false);
                                                                        }}
                                                                        sx={{ minHeight: 30 }}
                                                                    >
                                                                        Download
                                                                    </Button>
                                                                    <Tooltip title="Select file">
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    setSelected(entry);
                                                                                }}
                                                                            >
                                                                                <InsertDriveFileOutlinedIcon />
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {selected ? (
                                <Alert severity="info">
                                    Selected {selected.type}: {selected.path}
                                </Alert>
                            ) : null}
                        </Stack>
                    </Box>
                </Stack>
            </Stack>

            <Dialog open={preview.open} onClose={closePreview} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        {preview.kind === 'image' ? <ImageOutlinedIcon color="primary" /> : <PlayArrowRoundedIcon color="primary" />}
                        <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
                            {preview.entry?.name || 'Preview'}
                        </Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    {preview.kind === 'image' && preview.url ? (
                        <Box
                            component="img"
                            src={preview.url}
                            alt={preview.entry?.name || 'Selected image'}
                            sx={{
                                display: 'block',
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                mx: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                    ) : null}
                    {preview.kind === 'audio' && preview.url ? (
                        <Box component="audio" src={preview.url} controls autoPlay sx={{ width: '100%' }}>
                            <source src={preview.url} type={preview.mimeType} />
                        </Box>
                    ) : null}
                    {preview.kind === 'video' && preview.url ? (
                        <Box
                            component="video"
                            src={preview.url}
                            controls
                            autoPlay
                            sx={{
                                display: 'block',
                                width: '100%',
                                maxHeight: '70vh',
                                bgcolor: 'common.black',
                            }}
                        >
                            <source src={preview.url} type={preview.mimeType} />
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePreview}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={draft.open} onClose={closeAction} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {draft.action === 'create-folder'
                        ? 'Create Folder'
                        : draft.action === 'rename'
                          ? 'Rename Item'
                          : draft.action === 'move'
                            ? 'Move Item'
                            : 'Copy Item'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        {draft.action === 'create-folder' || draft.action === 'rename' ? (
                            <TextField
                                label={draft.action === 'create-folder' ? 'Folder Name' : 'New Name'}
                                value={draft.name}
                                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                                autoFocus
                            />
                        ) : (
                            <>
                                <FormControl fullWidth>
                                    <InputLabel id="folder-action-target-root-label">Target Root</InputLabel>
                                    <Select
                                        labelId="folder-action-target-root-label"
                                        label="Target Root"
                                        value={draft.targetRoot}
                                        onChange={(event) => setDraft((current) => ({ ...current, targetRoot: event.target.value }))}
                                    >
                                        {(roots.length > 0 ? roots : [{ root: 'input', path: 'input', exists: true }, { root: 'outputs', path: 'outputs', exists: true }, { root: 'generated', path: 'generated', exists: true }]).map((item) => (
                                            <MenuItem key={item.root} value={item.root}>
                                                {item.root}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Target Path"
                                    value={draft.targetPath}
                                    onChange={(event) => setDraft((current) => ({ ...current, targetPath: event.target.value }))}
                                    helperText="Use the full target path including the final file or folder name."
                                    autoFocus
                                />
                            </>
                        )}
                        {draft.action !== 'create-folder' ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={draft.overwrite}
                                        onChange={(event) => setDraft((current) => ({ ...current, overwrite: event.target.checked }))}
                                    />
                                }
                                label="Overwrite existing target"
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAction}>Cancel</Button>
                    <Button variant="contained" onClick={() => void saveAction()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
